import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Network, Plus } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { CardContent, CardIcon } from '@/components/ui/card'
import { TeamGroupsApi, type TeamGroup } from '@/services/users/teamGroupsApi'
import { apiErrorDescription } from '@/services/core/apiError'
import { walkAllPages, type ListSort } from '@/services/core/paging'
import { usePersistedState } from '@/hooks/usePersistedState'
import { AppShell } from '../../components/AppShell'
import { BulkActionBar } from '../../components/BulkActionBar'
import { ListCard } from '../../components/ListCard'
import { ListCardHeader } from '../../components/ListCardHeader'
import { SearchInput } from '../../components/SearchInput'
import { TeamGroupTable } from '../../components/TeamGroupTable'
import { StatusFilterButton } from '../../components/StatusFilter'
import { useUrlSearch } from '@/hooks/useUrlSearch'
import { useNetwork } from '@/contexts/NetworkContext'
import { ListPagination } from '../../components/ListPagination'
import { PageSizeSelect } from '../../components/PageSizeSelect'

export function TeamGroupsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [search, setSearch] = useUrlSearch()
  const { scopeKey, scopeReady } = useNetwork()
  const [statusFilter, setStatusFilter] = usePersistedState<'all' | 'ACTIVE' | 'INACTIVE'>(
    'filters.teamGroups.status',
    'ACTIVE'
  )
  const [page, setPage] = useState(1)
  const [sort, setSort] = useState<ListSort | null>(null)
  const [pageSize, setPageSize] = useState(25)
  const [selection, setSelection] = useState<TeamGroup[]>([])
  const [bulkLoading, setBulkLoading] = useState(false)

  // The list a page at a time, filtered and sorted as on screen.
  const fetchPage = (page: number, pageSize: number) =>
    TeamGroupsApi.getPage({ page, pageSize, search, status: statusFilter, sort })

  const { data, isLoading } = useQuery({
    queryKey: ['team-groups', scopeKey, search, statusFilter, page, pageSize, sort],
    queryFn: () => fetchPage(page, pageSize),
    enabled: scopeReady,
    staleTime: 5 * 60 * 1000,
  })
  const teamGroups = data?.results ?? []
  const totalCount = data?.totalCount ?? 0

  /** Back to the first page whenever the rows underneath change. */
  const refilter = (apply: () => void) => {
    apply()
    setPage(1)
    setSelection([])
  }

  const bulkSetStatus = async (state: 'ACTIVE' | 'INACTIVE') => {
    setBulkLoading(true)
    try {
      await Promise.all(
        selection.map((g) => TeamGroupsApi.update(g.id, { name: g.name, objectState: state }))
      )
      queryClient.invalidateQueries({ queryKey: ['team-groups'] })
      toast.success(t('common.done'), {
        description: t(
          state === 'ACTIVE'
            ? 'teamGroups.doneDetail_activate'
            : 'teamGroups.doneDetail_deactivate',
          { count: selection.length }
        ),
        duration: 3000,
      })
      setSelection([])
    } catch (e) {
      toast.error(t('common.error'), { description: apiErrorDescription(e), duration: 5000 })
    } finally {
      setBulkLoading(false)
    }
  }

  const handleSort = (next: ListSort | null) => {
    setSort(next)
    setPage(1)
  }

  return (
    <AppShell crumbs={[{ label: t('teamGroups.title') }]}>
      <div className="ad-page">
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <h1>{t('teamGroups.title')}</h1>
            <p>{t('teamGroups.subtitle')}</p>
          </div>
          <div className="ad-page__actions">
            <Button
              onClick={() =>
                navigate(
                  `/admin/team-groups/${crypto.randomUUID().replace(/-/g, '').slice(0, 16)}?new=true`
                )
              }
            >
              <Plus className="size-4" /> {t('teamGroups.newTeamGroup')}
            </Button>
          </div>
        </div>

        <ListCard fetchAll={() => walkAllPages(fetchPage)} tone="people">
          <ListCardHeader
            title={
              <>
                <CardIcon icon={Network} />
                {t('teamGroups.title')}
              </>
            }
            count={totalCount}
            actions={
              <PageSizeSelect
                value={pageSize}
                onChange={(size) => refilter(() => setPageSize(size))}
              />
            }
            controls={
              <>
                <SearchInput value={search} onChange={(v) => refilter(() => setSearch(v))} />
                <StatusFilterButton
                  value={statusFilter}
                  onChange={(v) => refilter(() => setStatusFilter(v))}
                />
              </>
            }
          />

          <BulkActionBar
            count={selection.length}
            actions={[
              {
                label: t('common.activate'),
                icon: 'pi pi-check-circle',
                severity: 'success',
                onClick: () => bulkSetStatus('ACTIVE'),
              },
              {
                label: t('common.deactivate'),
                icon: 'pi pi-ban',
                severity: 'danger',
                onClick: () => bulkSetStatus('INACTIVE'),
              },
            ]}
            onClear={() => setSelection([])}
            loading={bulkLoading}
          />

          <CardContent className="p-0">
            <TeamGroupTable
              teamGroups={teamGroups}
              loading={isLoading}
              selection={selection}
              onSelectionChange={setSelection}
              emptyMessage={t('teamGroups.noTeamGroupsFound')}
              sort={sort}
              onSortChange={handleSort}
            />
          </CardContent>

          <ListPagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            summary={t('teamGroups.teamGroupCount', { count: totalCount })}
          />
        </ListCard>
      </div>
    </AppShell>
  )
}
